import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DashboardPageFormField, DashboardPageFormToggleSection } from '@shigo-ui/dashboard-ui';
import { Checkbox, Input, Label } from '@shigo-ui/ui';

import { dashboardT, type DashboardMessageKey } from '../../../../../lib/messages';
import type { PlacementFormValue } from '../../placementFormSchema';
import { PlacementFieldLabel } from './PlacementFieldLabel';

export function PlacementPoddingSchainFields() {
  'use no memo';
  const { control, register, setValue, formState } = useFormContext<PlacementFormValue>();
  const poddingEnabled = useWatch({ control, name: 'poddingEnabled' });

  return (
    <>
      <DashboardPageFormToggleSection
        label={dashboardT('placements.podding')}
        checked={poddingEnabled}
        onCheckedChange={(checked) => {
          setValue('poddingEnabled', checked, { shouldDirty: true });
        }}
      >
        <div className="grid gap-5 md:grid-cols-2">
          {poddingFields.map(({ name, tooltip, suffix }) => (
            <DashboardPageFormField
              key={name}
              label={
                <PlacementFieldLabel
                  label={dashboardT(`placements.${name}`)}
                  tooltip={dashboardT(tooltip)}
                />
              }
              error={formState.errors[name]?.message}
            >
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  className={suffix ? 'pr-16' : undefined}
                  aria-invalid={Boolean(formState.errors[name])}
                  {...register(name)}
                />
                {suffix ? (
                  <span className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm">
                    {dashboardT(suffix)}
                  </span>
                ) : null}
              </div>
            </DashboardPageFormField>
          ))}
        </div>
      </DashboardPageFormToggleSection>

      <section className="border-input grid gap-3 rounded-md border p-4">
        <h2 className="text-sm font-semibold">{dashboardT('placements.schain')}</h2>
        <Controller
          control={control}
          name="schainIgnoreRequest"
          render={({ field }) => (
            <Label className="flex items-center gap-2 text-sm font-normal">
              <Checkbox
                checked={field.value}
                onCheckedChange={(checked) => {
                  field.onChange(checked === true);
                }}
              />
              {dashboardT('placements.schainIgnore')}
            </Label>
          )}
        />
        <Controller
          control={control}
          name="schainPreserveFirstOnly"
          render={({ field, fieldState }) => (
            <div>
              <Label className="flex items-center gap-2 text-sm font-normal">
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked === true);
                  }}
                />
                {dashboardT('placements.schainPreserve')}
              </Label>
              {fieldState.error ? (
                <p role="alert" className="text-destructive mt-1 text-xs">
                  {fieldState.error.message}
                </p>
              ) : null}
            </div>
          )}
        />
      </section>
    </>
  );
}

const poddingFields = [
  {
    name: 'maxPodSlotCount',
    tooltip: 'placements.tooltips.maxPodSlotCount',
    suffix: undefined,
  },
  { name: 'podFloor', tooltip: 'placements.tooltips.podFloor', suffix: undefined },
  {
    name: 'minPodDuration',
    tooltip: 'placements.tooltips.minPodDuration',
    suffix: 'placements.seconds',
  },
  {
    name: 'maxPodDuration',
    tooltip: 'placements.tooltips.maxPodDuration',
    suffix: 'placements.seconds',
  },
] as const satisfies ReadonlyArray<{
  name: 'maxPodSlotCount' | 'podFloor' | 'minPodDuration' | 'maxPodDuration';
  tooltip: DashboardMessageKey;
  suffix: DashboardMessageKey | undefined;
}>;
